import { useState } from "react";
import { isApiError, type ApiError } from "@/shared/api/errors";

/** What a flow does again once the details are saved: the same call, with the same choices. */
type Retry = () => unknown;

export interface BillingProfileStepState {
  /** True while the dialog shows the billing details instead of its own content. */
  open: boolean;
  /** The `BILLING_PROFILE_REQUIRED` that opened the step; it can name the one field the gateway wanted. */
  refusal: ApiError | null;
  /** The refused action is running again after a save. */
  continuing: boolean;
  /**
   * Hand a failure over. True when it was `BILLING_PROFILE_REQUIRED`: the step opens and keeps `retry`,
   * and the caller shows nothing else. False for anything else, which the caller handles as it always did.
   */
  intercept: (error: unknown, retry: Retry) => boolean;
  /** Back to the dialog's own content — its state is untouched, because it never unmounted. */
  back: () => void;
  /** Save done: run the refused action again. Its own success and failure handling decide what follows. */
  proceed: () => Promise<void>;
}

/**
 * The pending action behind "Save and continue" (Kumar, 2026-09-29).
 *
 * A purchase refused for want of billing details used to end in a red alert and a scroll to the card at
 * the foot of Settings → Subscription, and the customer started over. Now the dialog that was refused
 * turns into the billing details step, and saving them runs the refused call again — so a first-time
 * buyer presses one more button, not four.
 *
 * Each flow owns one of these next to its mutation, because only the flow knows what "again" means: the
 * renewal with the chosen period, the review with its two `when` answers, the add-on with its quantity.
 * The retry is the flow's ordinary call, so a success continues exactly as a first-time success would
 * (the order dialog opens) and closes the step on the way (`back`); a second refusal re-opens it with the
 * newer reason; any other failure is shown where that dialog always showed it.
 */
export function useBillingProfileStep(): BillingProfileStepState {
  const [pending, setPending] = useState<{ retry: Retry; refusal: ApiError } | null>(null);
  const [continuing, setContinuing] = useState(false);

  return {
    open: pending !== null,
    refusal: pending?.refusal ?? null,
    continuing,
    intercept: (error, retry) => {
      if (!isApiError(error) || error.code !== "BILLING_PROFILE_REQUIRED") return false;
      setPending({ retry, refusal: error });
      return true;
    },
    back: () => setPending(null),
    proceed: async () => {
      if (!pending) return;
      setContinuing(true);
      try {
        await pending.retry();
      } finally {
        setContinuing(false);
      }
    },
  };
}
